/** Spaces and the activities mounted on them. A space is durable; a mount is the bounded scope. */

import type {
  ActivityFanOutOutcome,
  AddedActivity,
  AgentPresence,
  SpaceAgentPresence,
  MountListFilter,
  MountPage,
  MountRecord,
  MountState,
  SpaceListFilter,
  SpacePage,
  SpacePauseResult,
  SpaceRecord,
  SpaceSummary,
  StartAllResult,
  StopAllResult,
} from "@nebulize/sdk";

import { api } from "./api";

export type {
  ActivityFanOutOutcome,
  AddedActivity,
  AgentPresence,
  SpaceAgentPresence,
  MountPage,
  MountRecord,
  SpacePage,
  SpacePauseResult,
  SpaceRecord,
  SpaceSummary,
  StartAllResult,
  StopAllResult,
};

/** The word the operator sees for a mount's lifecycle state. `mount` never reaches a label. */
const MOUNT_STATE_LABELS: Record<MountState, string> = {
  starting: "Starting",
  running: "Running",
  ending: "Stopping",
  ended: "Stopped",
};

/** Translate a `MountState` at the point of render. The one place this mapping is made. */
export function mountStateLabel(state: MountState | undefined): string {
  return state === undefined ? "Unknown" : MOUNT_STATE_LABELS[state];
}

export interface StartMountBody {
  added_activity_id: string;
}

/** SES-013. A blank name defaults, cloud-side, to the activity's own name. */
export interface AddActivityBody {
  activity_id: string;
  activity_version: string;
  name?: string;
  settings?: Record<string, unknown>;
}

/** One page of a collection read. Omit the cursor to start; a null one back means that was the last. */
export interface PageQuery {
  limit?: number;
  cursor?: string | null;
}

function query(
  params: Record<string, string | number | null | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "")
      search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered === "" ? "" : `?${rendered}`;
}

export const spaces = {
  create: (name = "") => api.post<SpaceRecord>("/v1/spaces", { name }),
  /** SES-011. Newest first, archived excluded unless asked for, each row saying whether it is live. */
  list: (state: SpaceListFilter = "active", page: PageQuery = {}) =>
    api.get<SpacePage>(`/v1/spaces${query({ state, ...page })}`),
  read: (spaceId: string) => api.get<SpaceRecord>(`/v1/spaces/${spaceId}`),
  archive: (spaceId: string) =>
    api.post<SpaceRecord>(`/v1/spaces/${spaceId}/archive`),

  /** The inverse of archive, which is what makes archiving reversible from a screen. */
  restore: (spaceId: string) =>
    api.post<SpaceRecord>(`/v1/spaces/${spaceId}/restore`),
  /** SES-012. Every machine connected to the space; no rows is a valid answer, not a failure. */
  agents: (spaceId: string) =>
    api.get<SpaceAgentPresence>(`/v1/spaces/${spaceId}/agents`),
  /** SES-013. Durable configuration, not yet live: a mount now always names one of these. */
  addActivity: (spaceId: string, body: AddActivityBody) =>
    api.post<AddedActivity>(`/v1/spaces/${spaceId}/added-activities`, body),
  /** Names the added activity to mount (item 2.3). You cannot mount what was never added. */
  mount: (spaceId: string, body: StartMountBody) =>
    api.post<MountRecord>(`/v1/spaces/${spaceId}/mounts`, body),
  /** SES-002 owns this read. SES-003 consumes it. Live by default, ended ones on request. */
  listMounts: (
    spaceId: string,
    state: MountListFilter = "live",
    page: PageQuery = {},
  ) =>
    api.get<MountPage>(
      `/v1/spaces/${spaceId}/mounts${query({ state, ...page })}`,
    ),
  readMount: (mountId: string) => api.get<MountRecord>(`/v1/mounts/${mountId}`),
  unmount: (mountId: string) =>
    api.post<MountRecord>(`/v1/mounts/${mountId}/end`),
  /** SES-006. Idempotent: pausing an already-paused mount returns it unchanged. */
  pause: (mountId: string) =>
    api.post<MountRecord>(`/v1/mounts/${mountId}/pause`),
  /** SES-006. Idempotent, and shifts every deadline the mount owns by the paused duration. */
  resume: (mountId: string) =>
    api.post<MountRecord>(`/v1/mounts/${mountId}/resume`),
  /** SES-006. The one space-level pause button: a fan-out reporting what it actually paused. */
  pauseSpace: (spaceId: string) =>
    api.post<SpacePauseResult>(`/v1/spaces/${spaceId}/pause`),
  /** Item 2.5. Start all: one result per episodic added activity, and nothing rolls back. A
   * resident activity is already running from the moment it was added and never appears here. */
  startAll: (spaceId: string) =>
    api.post<StartAllResult>(`/v1/spaces/${spaceId}/start`),
  /** Item 2.5. Stop all: inherits Stop's own terminality. A resident activity's mount has no Stop
   * control and never appears here. */
  stopAll: (spaceId: string) =>
    api.post<StopAllResult>(`/v1/spaces/${spaceId}/stop`),
  /** Item 2.5. Restart: Stop plus a new Start. Configuration and durable state survive; the run
   * does not, so the response is a fresh mount, never the ended one revived. */
  restart: (mountId: string) =>
    api.post<MountRecord>(`/v1/mounts/${mountId}/restart`),
};

/** Whether this space has anything running. The one fact a switcher row adds to the record. */
export function isSpaceLive(summary: SpaceSummary): boolean {
  return (summary.live_activity_count ?? 0) > 0;
}

/** Whether this mount still holds its hot state. Anything else has been unmounted. */
export function isLive(mount: MountRecord): boolean {
  return mount.state === "starting" || mount.state === "running";
}

/** Whether this mount is paused. Not a `MountState`: a paused mount is still `running`. */
export function isPaused(mount: MountRecord): boolean {
  return mount.paused_at != null;
}

/** Whether Restart is offered for this mount (item 2.5). */
export function canRestart(mount: MountRecord): boolean {
  return isLive(mount);
}
